import store from 'Store';
import CONFIG from 'Config';
import * as EVENTS from './events';
import {
  getSignUpParams,
  getSignUpSocialParams,
  getUniversalParams,
  getPIIProperties,
  getProfileProperties,
  getFarmParams,
  getInvestmentStepParams,
} from './params';

function triggerEvent(event, data = {}) {
  let baseParams;
  if (event === EVENTS.LINK_CLICKED || event === EVENTS.FORM_SUBMITTED) {
    baseParams = getUniversalParams(data);
  } else {
    baseParams = getUniversalParams();
  }

  window.analytics?.track(event, { ...data, ...baseParams });
}

function triggerIdentify(data = {}) {
  const baseParams = getUniversalParams();
  if (baseParams.user_id) {
    window.analytics?.identify(baseParams.user_id, { ...baseParams, ...data });
  } else {
    window.analytics?.identify({ ...baseParams, ...data });
  }
}

function triggerPage(event) {
  const baseParams = getUniversalParams();

  window.analytics?.page(event.fullPath, { ...baseParams });
}

function triggerSignUpStarted(data = {}) {
  triggerEvent(EVENTS.SIGN_UP, data);
}

function triggerSignUpCompleted(stepNumber, signUpData) {
  const params = getSignUpParams(stepNumber, signUpData);
  triggerIdentify({ ...params });
  triggerEvent(EVENTS.ACCOUNT_CREATED, { ...params });
}

function triggerSignUpSocialCompleted(signUpData = {}) {
  triggerIdentify({ ...signUpData });
  triggerEvent(EVENTS.ACCOUNT_CREATED, { ...signUpData });
}

function triggerSignUpStep(stepNumber, signUpData = {}) {
  const params = getSignUpParams(stepNumber, signUpData);
  triggerIdentify({ ...params });
  triggerEvent(EVENTS.SIGN_UP_STEP, { ...params });
}

function triggerSignUpSocialStep(stepNumber, signUpData = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const params = getSignUpSocialParams(stepNumber, signUpData);
  if (stepNumber === 3) {
    triggerSignUpSocialCompleted({ ...params, ...piiParams, ...profileParams });
  } else if (stepNumber !== 1) {
    triggerIdentify({ ...params, ...piiParams, ...profileParams });
    triggerEvent(EVENTS.SIGN_UP_STEP, { ...params, ...piiParams, ...profileParams });
  }
}

function triggerUserLogin(data = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();

  triggerIdentify({ ...piiParams, ...profileParams, ...data });
  triggerEvent(EVENTS.USER_LOGIN, { ...piiParams, ...profileParams, ...data });
}

function triggerUserLogout() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.SIGNED_OUT, { ...piiParams, ...profileParams });
}

function triggerFarmView() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams();

  triggerEvent(EVENTS.VIEW_FARM, { ...piiParams, ...profileParams, ...farmParams });
}

function triggerFarmClick() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams();
  triggerEvent(EVENTS.FARM_CLICKED, { ...piiParams, ...profileParams, ...farmParams });
}

function triggerNewInvestment(farmParams = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams, ...farmParams });
  triggerEvent(EVENTS.INVESTMENT_CONFIRMATION, { ...piiParams, ...profileParams, ...farmParams });
}

function triggerInvestmentStep(data = {}) {
  const piiParams = getPIIProperties(data);
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams(data);

  if (data.investment_process_step < 6) {
    triggerIdentify({ ...piiParams, ...profileParams, ...farmParams });
    triggerEvent(EVENTS.INVESTMENT_STEP, { ...piiParams, ...profileParams, ...farmParams });
  } else {
    triggerNewInvestment({ step: data.investment_process_step, ...piiParams, ...profileParams, ...farmParams });
  }
}

function triggerInvestmentStart() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams();
  triggerIdentify({ ...piiParams, ...profileParams, ...farmParams });
  triggerEvent(EVENTS.START_INVESTMENT, { ...piiParams, ...profileParams, ...farmParams });
}

function triggerPaymentInfoEntered(data = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams(data);

  triggerIdentify({ ...piiParams, ...profileParams, ...farmParams });
  triggerEvent(EVENTS.PAYMENT_INFO_ENTERED, { ...piiParams, ...profileParams, ...farmParams });
}

function triggerCancelInvestment() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams();
  triggerEvent(EVENTS.ORDER_CANCELLED, { ...piiParams, ...profileParams, ...farmParams });
}

function triggerLinkedBankAccount(data) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();

  triggerIdentify({ ...data, ...piiParams, ...profileParams });
  triggerEvent(EVENTS.LINKED_BANK_ACCOUNT, { ...data, ...piiParams, ...profileParams });
}

function triggerAccreditedAndLinkedBankAccount() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.ACCREDITED_AND_LINKED_BANK_ACCOUNT, { ...piiParams, ...profileParams });
}

function triggerAccreditationAfterRegister() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.ACCREDITATION_AFTER_REGISTER, { ...piiParams, ...profileParams });
}

function triggerAccreditedOnce() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.ACCREDITED_ONCE, { ...piiParams, ...profileParams });
}

function triggerAccreditationExpired() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.ACCREDITATION_EXPIRED, { ...piiParams, ...profileParams });
}

function triggerAccreditationStarted() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.ACCREDITATION_STARTED, { ...piiParams, ...profileParams });
}

function triggerAccreditationSubmitted() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.ACCREDITATION_SUBMITTED, { ...piiParams, ...profileParams });
}

function triggerAccreditationVerified() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.ACCREDITED_VERIFY, { ...piiParams, ...profileParams });
}

function triggerInvestmentAccountStart(data = {}) {
  const piiParams = getPIIProperties(data);
  const profileParams = getProfileProperties();
  // triggerIdentify({ ...data, ...piiParams, ...profileParams });
  triggerEvent(EVENTS.INVESTMENT_ACCOUNT_START, { ...data, ...piiParams, ...profileParams });
}

function triggerInvestmentAccountStep(data = {}) {
  const piiParams = getInvestmentStepParams(data.step, data);
  const profileParams = getProfileProperties();
  triggerIdentify({ type: data.type, ...piiParams, ...profileParams });
  triggerEvent(EVENTS.INVESTMENT_ACCOUNT_STEP, { type: data.type, ...piiParams, ...profileParams });
}

function triggerInvestmentAccountCreated(data = {}) {
  const piiParams = getInvestmentStepParams(data.step, data);
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.INVESTMENT_ACCOUNT_CREATED, { ...piiParams, ...profileParams });
}

function triggerPromotionViewed(data = {}) {
  triggerEvent(EVENTS.PROMOTION_VIEWED, { ...data });
}

function triggerPromotionClicked(data = {}) {
  triggerEvent(EVENTS.PROMOTION_CLICKED, { ...data });
}

function triggerPersonVerified(formIdentification) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerIdentify({ ...piiParams, ...profileParams });
  triggerEvent(EVENTS.IDENTITY_VERIFIED, { formIdentification, ...piiParams, ...profileParams });
}

function triggerFarmOwnerGetStarted() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.FARM_OWNER_GET_STARTED, { ...piiParams, ...profileParams });
}

function triggerExplainerWatchStart() {
  triggerEvent(EVENTS.EXPLAINER_WATCH_START);
}

function triggerExplainerWatchEnd(data) {
  triggerEvent(EVENTS.EXPLAINER_WATCH_END, data);
}

function triggerLinkClicked(data = {}) {
  triggerEvent(EVENTS.LINK_CLICKED, data);
}

function triggerHSFormSubmit(formIdentification) {
  triggerEvent(EVENTS.HUBSPOT_FORM_SUBMIT, formIdentification);
}

function triggerFormSubmit(event, data) {
  triggerEvent(EVENTS.FORM_SUBMITTED, { form_name: event, ...data });
  if (event === 'at.verifyAccreditation') {
    triggerAccreditationStarted();
  }
  triggerIdentify({ form_name: event, ...data });
}

function triggerDocumentViewed(event, data) {
  triggerEvent(EVENTS.DOCUMENT_VIEWED, { ...event, ...data });
}

function triggerPPMDocumentViewed(data = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();

  triggerEvent(EVENTS.PPM_DOCUMENT_VIEWED, { ...data, ...piiParams, ...profileParams });
}

function triggerAllTextAlerts(data) {
  const farmParams = getFarmParams();
  triggerEvent(EVENTS.ENROLLED_IN_TEXT_ALERTS, { ...data, ...farmParams });
}

function triggerClickedVerifyAccreditation() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.CLICKED_VERIFY_ACCREDITATION, { ...piiParams, ...profileParams });
}

function triggerClickedUpdateAccreditation() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.CLICKED_UPDATE_ACCREDITATION, { ...piiParams, ...profileParams });
}

function triggerClickedExtendAccreditation() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.CLICKED_EXTEND_ACCREDITATION, { ...piiParams, ...profileParams });
}

function triggerBeginInvesting(data) {
  triggerEvent(EVENTS.BEGIN_INVESTING, data);
}

function triggerAddAdvisorStarted() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.WAA_ADD_ADVISOR_STARTED, { ...piiParams, ...profileParams });
}

function triggerAddAdvisorCompleted(data = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.WAA_ADD_ADVISOR_COMPLETED, { advisor: data, ...piiParams, ...profileParams });
}

function triggerWAAAskMeLater() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.WAA_ASK_ME_LATER, { ...piiParams, ...profileParams });
}
function triggerWAADontAskAgain() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.WAA_DONT_ASK_AGAIN, { ...piiParams, ...profileParams });
}

function triggerRemoveAdvisor(data = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.WAA_REMOVED_ADVISOR, { advisor: data, ...piiParams, ...profileParams });
}

function triggerShareButtonClicked(title, refId) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(title, { ...piiParams, ...profileParams, refId });
}

function triggerContactTeamClicked(event, option) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams();
  triggerEvent(event, { ...piiParams, ...profileParams, ...farmParams, option });
}

function triggerBookCallIR() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  const farmParams = getFarmParams();
  triggerEvent(EVENTS.BOOK_CALL_IR, { ...piiParams, ...profileParams, ...farmParams });
}

function triggerAdvisorAttestation(data = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.ADVISOR_ATTESTATION, { ...piiParams, ...profileParams, ...data });
}

function triggerToggleAutomaticVerification(data = {}) {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.TOGGLED_AUTOMATIC_VERIFICATION, { ...piiParams, ...profileParams, ...data });
}

function triggerViewedRealizedTrackRecord() {
  const piiParams = getPIIProperties();
  const profileParams = getProfileProperties();
  triggerEvent(EVENTS.VIEWED_REALIZED_TRACK_RECORD, { ...piiParams, ...profileParams });
}

function triggerHSPageView(route) {
  const _hsq = window._hsq || [];
  if (store.getters['login/isLoggedIn']) {
    const { email } = store.getters['profile/profile'];
    _hsq.push(['identify', { email }]);
  } else {
    _hsq.push(['identify']);
  }
  _hsq.push(['setPath', route.fullPath]);
  _hsq.push(['trackPageView']);
}

function triggerSiftPageView() {
  const _sift = window._sift = window._sift || []; // eslint-disable-line
  _sift.push(['_setAccount', CONFIG.siftAccount]);
  if (store.getters['login/isLoggedIn']) {
    const { email } = store.getters['profile/profile'];
    _sift.push(['_setUserId', email]);
  }
  _sift.push(['_trackPageview']);
}

function onPageResolved(route) {
  setTimeout(() => {
    triggerSiftPageView();
    triggerHSPageView(route);
  }, 1500);
}

export default {
  EVENTS,
  onPageResolved,
  triggerSignUpCompleted,
  triggerSignUpStarted,
  triggerSignUpStep,
  triggerSignUpSocialStep,
  triggerHSFormSubmit,
  triggerEvent,
  triggerLinkedBankAccount,
  triggerCancelInvestment,
  triggerNewInvestment,
  triggerAccreditedAndLinkedBankAccount,
  triggerAccreditationAfterRegister,
  triggerAccreditedOnce,
  triggerAccreditationExpired,
  triggerAccreditationStarted,
  triggerAccreditationSubmitted,
  triggerAccreditationVerified,
  triggerPersonVerified,
  triggerExplainerWatchStart,
  triggerExplainerWatchEnd,
  triggerPage,
  triggerFormSubmit,
  triggerDocumentViewed,
  triggerPPMDocumentViewed,
  triggerBeginInvesting,
  triggerLinkClicked,
  triggerInvestmentStep,
  triggerInvestmentStart,
  triggerPaymentInfoEntered,
  triggerUserLogin,
  triggerUserLogout,
  triggerFarmView,
  triggerFarmClick,
  triggerPromotionViewed,
  triggerPromotionClicked,
  triggerInvestmentAccountStart,
  triggerInvestmentAccountStep,
  triggerInvestmentAccountCreated,
  triggerAllTextAlerts,
  triggerClickedVerifyAccreditation,
  triggerClickedUpdateAccreditation,
  triggerClickedExtendAccreditation,
  triggerFarmOwnerGetStarted,
  triggerAddAdvisorStarted,
  triggerAddAdvisorCompleted,
  triggerWAAAskMeLater,
  triggerWAADontAskAgain,
  triggerRemoveAdvisor,
  triggerShareButtonClicked,
  triggerContactTeamClicked,
  triggerBookCallIR,
  triggerAdvisorAttestation,
  triggerToggleAutomaticVerification,
  triggerViewedRealizedTrackRecord,
};
